/**
 * Supplier directory resolution — merges baseline JSON, platform curated
 * Firestore overlay, and per-company additions.
 *
 * Precedence (later wins on id collision):
 *   1. SUPPLIER_BASELINES (offline fallback)
 *   2. curated_suppliers (platform admin)
 *   3. companies/{companyId}/suppliers (company admin)
 */
import type { SCMCategory, Supplier } from '../types/supplyChain';
import { SUPPLIER_BASELINES } from './supplyChainBaselines';
import { mergeStrategicPartnerOverlay } from './strategicPartnerSeed';
import {
  fetchCuratedSuppliers,
  fetchCompanySuppliers,
} from '../services/supplyChain/curatedSupplierStore';

/** Pure merge — used in tests and when callers already fetched overlays. */
export function mergeSupplierDirectory(
  baseline: readonly Supplier[],
  curated: readonly Supplier[],
  company: readonly Supplier[],
): Supplier[] {
  const byId = new Map<string, Supplier>();
  for (const s of baseline) byId.set(s.id, s);
  for (const s of curated) byId.set(s.id, s);
  for (const s of company) byId.set(s.id, s);
  return Array.from(byId.values()).map(mergeStrategicPartnerOverlay);
}

/** Async resolve with Firestore overlays. Falls back to baseline-only on fetch failure. */
export async function resolveSupplierDirectory(
  companyId?: string | null,
): Promise<Supplier[]> {
  const [curated, company] = await Promise.all([
    fetchCuratedSuppliers(),
    fetchCompanySuppliers(companyId),
  ]);
  return mergeSupplierDirectory(SUPPLIER_BASELINES, curated, company);
}

export function getSupplierFromDirectory(
  directory: readonly Supplier[],
  supplierId: string,
): Supplier | undefined {
  return directory.find((s) => s.id === supplierId);
}

export function suppliersByCategoryFromDirectory(
  directory: readonly Supplier[],
  category: SCMCategory,
): Supplier[] {
  return directory.filter((s) => s.categories.includes(category));
}
